<script>
  import { onMount, onDestroy } from 'svelte';
  import { get } from 'svelte/store';
  import {
    clearBrowserOfflineState,
    flashStore,
    runFlashAction,
    initApp,
    loadUsers,
    resumeApp,
    setBrowserOfflineState,
    startConnectivityWatchdog,
    startMqtt,
    stopConnectivityWatchdog,
    userStore,
    usersStore,
  } from './lib/stores.js';
  import {
    describeBootstrapError,
    isResumeTimeout,
    nextBootstrapRetryDelay,
    shouldShowResumeError,
    shouldShowResumeLoading,
    withResumeWatchdog,
  } from './lib/app-resume.js';
  import { initViewportTracking } from './lib/viewport.js';
  import { LOGIN_REQUIRED_EVENT, resetLoginRedirect } from './lib/api.js';
  import Router from './lib/Router.svelte';
  import ConnectionStatus from './lib/components/ConnectionStatus.svelte';
  import LoginScreen from './lib/components/LoginScreen.svelte';
  import { publishReturnTarget } from './lib/return-to.js';
  import BuildStamp from './lib/components/BuildStamp.svelte';

  let loading = true;
  let resuming = false;
  let error = null;
  let errorTitle = 'Ошибка';
  let resumeInFlight = false;
  let wasHidden = false;
  // Этап 16: 401 от /api → экран входа поверх приложения (Face ID или сертификат).
  let loginRequired = false;

  function handleLoginRequired() {
    loginRequired = true;
    stopBootstrapRetry();
  }

  // Успешный вход по Face ID: кука уже стоит — поднимаем приложение заново без
  // перезагрузки страницы (bootstrapApp сам решит, холодный это старт или нет).
  async function handleLoggedIn() {
    loginRequired = false;
    resetLoginRedirect();
    loading = true;
    error = null;
    await bootstrapApp();
  }

  // Слушатель ставим до первого запроса: холодный старт с истёкшей кукой даёт 401
  // ещё в bootstrapApp, раньше onMount — событие иначе терялось.
  if (typeof window !== 'undefined') {
    window.addEventListener(LOGIN_REQUIRED_EVENT, handleLoginRequired);
  }

  // Авто-ретрай экрана «Ошибка запуска»: backoff 5→10→20→40→60с (nextBootstrapRetryDelay)
  // с видимым обратным отсчётом. Ретрай = handleResume() — его гарды (resumeInFlight,
  // visible, hadBlockingScreen→bootstrapApp) исключают параллельные bootstrap'ы.
  let retryAttempt = 0;
  let retryCountdown = 0;
  let retryTimer = null;

  function stopBootstrapRetry() {
    if (retryTimer) {
      clearInterval(retryTimer);
      retryTimer = null;
    }
    retryCountdown = 0;
  }

  function scheduleBootstrapRetry() {
    stopBootstrapRetry();
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return; // в фоне не жжём попытки — visible сам вызовет handleResume
    }
    retryCountdown = Math.round(nextBootstrapRetryDelay(retryAttempt) / 1000);
    retryTimer = setInterval(() => {
      retryCountdown -= 1;
      if (retryCountdown > 0) {
        return;
      }
      stopBootstrapRetry();
      retryAttempt += 1;
      handleResume();
    }, 1000);
  }

  // Экран ошибки появился/остался — планируем следующую попытку; ушёл — сбрасываем.
  $: if (error && !loading && !resuming) {
    if (!retryTimer) {
      scheduleBootstrapRetry();
    }
  } else if (!error && retryTimer) {
    stopBootstrapRetry();
    retryAttempt = 0;
  }

  async function bootstrapApp() {
    try {
      await initApp();
      // Этап 26.1: пришли со входа для сайта или git владельца — сессия есть, возвращаем.
      const back = publishReturnTarget(window.location.search, window.location.hostname);
      if (back) {
        window.location.replace(back);
        return;
      }
      // Запуск открывает список чатов (решение владельца 2026-09-29): последний
      // открытый чат не восстанавливается. Перезагрузка остаётся на своём экране —
      // его хранит адрес.
      if (!window.location.hash) {
        window.location.hash = '/chats';
      }
      error = null;
      errorTitle = 'Ошибка';
      // MQTT поднимаем ПОСЛЕ старта приложения (ленивый тяжёлый чанк) — не блокирует рендер.
      startMqtt();
    } catch (e) {
      errorTitle = 'Ошибка запуска';
      error = describeBootstrapError(e);
    } finally {
      loading = false;
    }
  }

  async function isConnectionAlive() {
    // Короткий пинг /api/whoami: отличаем ЖИВОЕ соединение от «мёртвого» после
    // iOS-фона (WebKit держит замороженные сокеты, fetch/WSS виснут). Любой
    // HTTP-ответ (даже 401) = соединение живо. Таймаут/обрыв = мёртвое.
    if (typeof fetch === 'undefined') {
      return true;
    }
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), 4000) : null;
    try {
      await fetch('/api/whoami', { credentials: 'same-origin', cache: 'no-store', signal: controller?.signal });
      return true;
    } catch {
      return false;
    } finally {
      if (timer) {
        clearTimeout(timer);
      }
    }
  }

  async function handleResume() {
    const isVisible = typeof document === 'undefined' || document.visibilityState === 'visible';
    if (resumeInFlight || !isVisible) {
      return;
    }

    const isOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
    if (!isOnline) {
      setBrowserOfflineState();
      return;
    }

    resumeInFlight = true;
    const hadBlockingScreen = loading || Boolean(error);
    try {
      clearBrowserOfflineState();
      if (hadBlockingScreen) {
        // Холодное восстановление (был блокирующий экран загрузки/ошибки):
        // показываем спиннер и watchdog, при провале — экран ошибки.
        resuming = shouldShowResumeLoading({ loading, error, isOnline, isVisible, resumeInFlight: false });
        if (error || !loading) {
          loading = true;
        }
        await withResumeWatchdog(bootstrapApp());
      } else {
        // Тёплый resume. На iOS соединения после фона часто «мертвы» — fetch/WSS виснут,
        // не доходя до сервера. Если пинг НЕ прошёл — делаем полный window.location.reload():
        // он даёт свежий сетевой стек и ЧИСТОЕ MQTT-переподключение (один connect на новую
        // страницу). Пересоздавать MQTT-клиент в живущей сессии (resumeApp→ensureConnected)
        // на нестабильном iOS-WSS НЕЛЬЗЯ — это плодит недозакрытые соединения и зацикливает
        // reconnect-storm (см. mqtt.js) → флап MQTT. Визуального «флапа» от reload почти нет:
        // экран чата мгновенно гидрируется из localStorage-снимка (message-cache).
        const alive = await isConnectionAlive();
        if (!alive) {
          window.location.reload();
          return;
        }
        await resumeApp();
      }
      if (!error) {
        wasHidden = false;
      }
    } catch (e) {
      console.warn('Failed to resume application', e);
      if (shouldShowResumeError(e, { hadBlockingScreen })) {
        errorTitle = isResumeTimeout(e) ? 'Ошибка восстановления' : 'Ошибка';
        error = describeBootstrapError(e);
      }
      loading = false;
    } finally {
      resuming = false;
      resumeInFlight = false;
    }
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      wasHidden = true;
      stopBootstrapRetry(); // в фоне отсчёт замирает; visible сделает немедленный ретрай
      return;
    }
    if (document.visibilityState === 'visible') {
      handleResume();
    }
  }

  function handlePageShow(event) {
    if (event.persisted || document.visibilityState === 'visible') {
      handleResume();
    }
  }

  function handleOnline() {
    handleResume();
  }

  function handleFocus() {
    if (!wasHidden) {
      return;
    }
    handleResume();
  }

  function handleOffline() {
    setBrowserOfflineState();
  }

  onMount(async () => {
    // Высота видимого вьюпорта (iOS-клавиатура) → CSS-переменная --app-height.
    initViewportTracking();
    await bootstrapApp();
    // Страховка от потерянного 'online' на iOS: видимая сессия без MQTT ≥60с при
    // живой сети — форс переподключения (см. stores.startConnectivityWatchdog).
    startConnectivityWatchdog();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  });

  onDestroy(() => {
    stopBootstrapRetry();
    stopConnectivityWatchdog();
    window.removeEventListener(LOGIN_REQUIRED_EVENT, handleLoginRequired);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pageshow', handlePageShow);
    window.removeEventListener('focus', handleFocus);
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  });
</script>

<main class="app">
  {#if $flashStore}
    <div class="flash" role="status" class:error={$flashStore.level === 'error'} class:warning={$flashStore.level === 'warning'}>
      {$flashStore.message}
      {#if $flashStore.action}
        <button type="button" class="flash-action" on:click={runFlashAction}>{$flashStore.action.label}</button>
      {/if}
    </div>
  {/if}
  {#if loginRequired}
    <LoginScreen on:login={handleLoggedIn} />
  {:else if loading || resuming}
    <div class="loading">
      <div class="spinner"></div>
      <p>{resuming ? 'Восстанавливаем приложение...' : 'Загрузка...'}</p>
    </div>
  {:else if error}
    <div class="error-screen">
      <h2>{errorTitle}</h2>
      <p>{error}</p>
      {#if retryCountdown > 0}
        <p class="retry-countdown">Повторим через {retryCountdown} с</p>
      {/if}
      <button on:click={() => location.reload()}>Обновить</button>
      <BuildStamp />
    </div>
  {:else}
    <ConnectionStatus />
    <Router />
  {/if}
</main>

<style>
  .app {
    /* --app-height пишет lib/viewport.js (visualViewport, iOS-клавиатура);
       фолбэк 100dvh — до первого замера и без visualViewport. */
    height: var(--app-height, 100dvh);
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* Экран во время свайпа уезжает transform'ом за край — горизонтального скролла
       у приложения быть не должно (clip не создаёт scroll-контейнер, hidden — фолбэк). */
    overflow-x: hidden;
    overflow-x: clip;
    background: var(--bg-primary);
    color: var(--text-primary);
  }

  .loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;
    gap: 1rem;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-color);
    border-top-color: var(--accent-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;
    gap: 1rem;
    padding: 2rem;
    text-align: center;
  }

  .retry-countdown {
    color: var(--text-secondary);
    font-size: 0.9rem;
  }

  .error-screen button {
    padding: 0.75rem 2rem;
    background: var(--accent-color);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
  }

  .flash {
    /* Оверлей поверх контента: показ/скрытие тоста не сдвигает лейаут (CLS). */
    position: fixed;
    top: calc(0.75rem + env(safe-area-inset-top, 0px));
    left: 50%;
    transform: translateX(-50%);
    z-index: 200;
    max-width: min(92vw, 480px);
    padding: 0.75rem 1rem;
    border-radius: 10px;
    box-shadow: var(--shadow-md);
    background: var(--bg-elevated);
    color: var(--success-color);
    text-align: center;
    font-size: 0.875rem;
  }

  .flash-action {
    margin-left: 0.75rem;
    min-height: 32px;
    padding: 0.25rem 0.75rem;
    border: 1px solid currentColor;
    border-radius: 16px;
    background: transparent;
    color: inherit;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }

  .flash.warning {
    background: var(--warning-bg);
    color: var(--warning-text);
  }

  .flash.error {
    background: var(--error-bg);
    color: var(--error-text);
  }
</style>
